import cx from "classnames";
import { useCustomClassContext } from "../../../hooks/useCustomClassContext/CustomClassContext";
import NavbarDropdown from "../../Block/NavbarDropdown/NavbarDropdown";
import { SocialIconProps } from "../../Block/SocialIcon/SocialIcon";
import Button from "../../Element/Button";
import Icon from "../../Element/Icon/icon";
import Logo from "../../Element/Logo/Logo";
import ProgressBar from "../../Element/ProgressBar";
import * as Typography from "../../Element/Typography";
import { BaseDataConfig } from "../../Layout/Base/types";
import { RoutePathsType } from "../../Utility/dataTypes";
import MegaMenu from "../MegaMenu/MegaMenu";
import styles from "./navbargrid.module.css";
import { NavigationProps } from "./types";

const { NavLink, Heading } = Typography;

type MegaMenuPropsType = React.ComponentProps<typeof MegaMenu>;

type NavbarGridProps = {
  navObject: NavigationProps;
  sticky: boolean;
  currentLocation: { pathname: string };
  // Optional MEGAMENU props
  contentHubProps?: {
    title?: string;
    path?: string;
  }[];
  SubscribeBoxProps?: MegaMenuPropsType["SubscribeBoxProps"];
  socialIconProps?: SocialIconProps;
  footerProps?: RoutePathsType["Footer"];
  // Optional BANNER props
  bannerConfig?: NonNullable<BaseDataConfig>["mobileBanner"];
};

const NavbarGrid = ({ navObject, sticky }: NavbarGridProps) => {
  const {
    LogoSource,
    routes,
    SearchIcon,
    MenuIcon,
    toggleSidebar,
    toggleSearchPopup,
    ArticleTitle,
    section,
    navbarDropdownOptions,
    currentInViewArticleRef,
    readProgress,
  } = navObject;

  const customClass = useCustomClassContext();
  return (
    <div className={styles.persistent}>
      <div
        className={cx(
          styles.header,
          styles.headerTemplate,
          sticky && styles.sticky,
          ArticleTitle && styles.title,
        )}
      >
        {/* grid-area: logo; */}
        <div
          className={cx(
            styles.areaLogo,
            styles.logo,
            customClass?.navbar?.logo,
          )}
        >
          <Logo
            href="/"
            src={LogoSource}
            width="100%"
            height="100%"
            alt="logo"
          />
        </div>

        {/* grid-area: pri; */}
        {(!sticky || !ArticleTitle) && (
          <div
            className={cx(
              styles.areaPri,
              styles.primary_items,
              customClass?.navbar?.primaryItems,
            )}
            data-layout-position="navbar"
          >
            {routes.primaryLinks.map((item, index) => {
              if (item.as === "button") {
                return (
                  <Button
                    key={index}
                    variant="primary"
                    href={item.url}
                    target={item.target}
                  >
                    {item.name}
                  </Button>
                );
              }

              if (item.as === "dropdown") {
                return (
                  <NavbarDropdown
                    key={index}
                    dropdownList={item.subMenu}
                    optionsDropdown={navbarDropdownOptions}
                  >
                    <NavLink
                      href={item.url}
                      customClassName={customClass?.navLinks?.itemLink}
                    >
                      {item.name}
                    </NavLink>
                  </NavbarDropdown>
                );
              }

              return (
                <NavLink
                  key={index}
                  href={item.url}
                  customClassName={cx(
                    customClass?.navLinks?.verticalLineHeader,
                    customClass?.navLinks?.itemLink,
                  )}
                >
                  {item.name}
                </NavLink>
              );
            })}
          </div>
        )}

        {/* grid-area: pri; */}
        {sticky && ArticleTitle && (
          <div className={styles.areaPri}>
            {section && (
              <a
                href={section.path}
                className={cx(
                  styles.articleSection,
                  customClass?.navbar?.articleSection,
                )}
              >
                <Typography.Label>{section.name}</Typography.Label>
              </a>
            )}
            <div className={styles.article_item}>
              <Heading.SubHeading4>{ArticleTitle}</Heading.SubHeading4>
            </div>
          </div>
        )}
        {/* grid-area: util; */}
        {!!routes.utilityLinks.length && (
          <div
            className={cx(styles.areaUtil, styles.utility_items)}
            data-layout-position="navbar"
          >
            {routes.utilityLinks.map((item, index) => {
              if (item.as === "text") {
                return (
                  <NavLink key={index} href={item.url}>
                    {item.name}
                  </NavLink>
                );
              }
              return (
                <Button
                  key={index}
                  variant={item?.variant || "primary"}
                  href={item.url}
                  target={item.target}
                >
                  {item.name}
                </Button>
              );
            })}
          </div>
        )}

        {/* grid-area: search; */}
        <div className={styles.areaSearch}>
          <a
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) {
                return;
              }

              if (routes.searchBarLink?.as === "popup" && toggleSearchPopup) {
                toggleSearchPopup();
                e.preventDefault();
                return;
              }
            }}
            href={routes.searchBarLink.url}
            title={routes.searchBarLink.name}
            className={styles.searchIcon}
          >
            <Icon>{SearchIcon}</Icon>
          </a>
        </div>

        {/* grid-area: menu; */}
        <div className={styles.areaMenu}>
          <button
            onClick={toggleSidebar}
            className={styles.hamburgerMenuBtnIcon}
            title="Menu"
            data-layout-position="menubutton"
          >
            <Icon>{MenuIcon}</Icon>
          </button>
        </div>
      </div>
      {readProgress && currentInViewArticleRef && (
        <ProgressBar containerRef={currentInViewArticleRef} />
      )}
    </div>
  );
};

export default NavbarGrid;
